import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HomeworkGuyLogo from './HomeworkGuyLogo';
import OptimizedImage from './OptimizedImage';
import { faTimes, faBars, faGraduationCap, faBook, faCalculator, faFlask, faCode, faChartLine, faLanguage, faHistory, faPencilAlt, faChevronRight, faChalkboardTeacher, faLaptop, faFilePdf, faTv, faDatabase, faFileInvoiceDollar, faClock, faBookOpen, faSearch, faDesktop, faPen, faSquareRootAlt, faChartBar, faAtom, faChevronDown, faMagic, faUserMd, faVideo, faFileAlt, faDollarSign, faShieldAlt, faUniversity } from '@fortawesome/free-solid-svg-icons';
import '../styles/Navigation.scss';
import { BASE_URL } from '../utils/SEOUtils';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('/');
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showResourcesDropdown, setShowResourcesDropdown] = useState(false);
  const [hoveredSubmenu, setHoveredSubmenu] = useState<string | null>(null);
  const [mobileNestedMenuOpen, setMobileNestedMenuOpen] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  
  // Check if we're on the Order page
  const isOrderPage = location.pathname === '/order' || location.pathname === 'order';

  // Update active link when location changes
  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  // Handle scroll effect for sticky header
  useEffect(() => {
    // If on Order page, always set scrolled to true
    if (isOrderPage) {
      setScrolled(true);
      return;
    }
    
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Initial call to set the correct state on mount
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOrderPage]); // Add isOrderPage as a dependency

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu when location changes and handle navigation cleanup
  useEffect(() => {
    const cleanup = () => {
      document.body.classList.remove('order-page-active');
      document.body.classList.remove('textarea-focused');
      document.body.classList.remove('no-scroll');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, 0);
    };

    // Close mobile menu on route change
    setIsOpen(false);
    setMobileServicesOpen(false);
    setMobileResourcesOpen(false);
    setMobileNestedMenuOpen(null);
    
    // Close services dropdown menu on route change
    setShowServicesDropdown(false);
    
    // Close resources dropdown menu on route change
    setShowResourcesDropdown(false);
    
    // Handle navigation cleanup when route changes
    if (!isOrderPage) {
      cleanup();
    }
  }, [location, isOrderPage]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setMobileServicesOpen(false); // Close services when toggling main menu
    setMobileResourcesOpen(false); // Close resources when toggling main menu
    // Prevent body scrolling when menu is open
    document.body.style.overflow = !isOpen ? 'hidden' : '';
  };

  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen);
    // Close nested menus when closing main services menu
    if (mobileServicesOpen) {
      setMobileNestedMenuOpen(null);
    }
  };

  const toggleMobileNestedMenu = (menuKey: string) => {
    setMobileNestedMenuOpen(prev => prev === menuKey ? null : menuKey);
  };

  const toggleMobileResources = () => {
    setMobileResourcesOpen(!mobileResourcesOpen);
  };

  const closeMobileMenu = () => {
    setIsOpen(false);
    setMobileServicesOpen(false);
    setMobileResourcesOpen(false);
    setMobileNestedMenuOpen(null);
    document.body.style.overflow = '';
  };
  
  // Force navigation - only used from Order page
  const handleDirectNavigation = (path: string, e: React.MouseEvent) => {
    if (isOrderPage) {
      e.preventDefault();
      
      // Force cleanup of Order page classes
      document.body.classList.remove('order-page-active');
      document.body.classList.remove('textarea-focused');
      document.body.classList.remove('no-scroll');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Reset scroll position
      window.scrollTo(0, 0);
      
      // Create navigation path with clean URLs
      const baseUrl = window.location.origin;
      // If path is '/' set it to empty string for the correct root URL
      const cleanPath = path === '/' ? '' : path.startsWith('/') ? path.substring(1) : path;
      const targetPath = `${baseUrl}/${cleanPath}`;
      
      // Use window.location for most reliable navigation from Order page
      window.location.href = targetPath;
      
      return true;
    }
    
    // For non-Order pages, just close mobile menu and let React Router handle navigation
    closeMobileMenu();
    return false;
  };

  // Handle normal navigation (non-Order page)
  const handleNavigation = () => {
    closeMobileMenu();
  };

  // Enhanced navigation handler
  const handleLinkClick = (path: string, e: React.MouseEvent) => {
    if (isOrderPage) {
      return handleDirectNavigation(path, e);
    }
    
    // For non-Order pages, just close mobile menu and let React Router handle it
    closeMobileMenu();
  };

  // Define service categories with items
  const serviceCategories = [
    {
      title: "Coursework Help",
      icon: faLaptop,
      items: [
        {
          title: "Affordable Accounting Class Help",
          link: "/affordable-accounting-class-help",
          icon: faCalculator
        },
        {
          title: "Hire Someone To Do My Calculus Class",
          link: "/hire-someone-to-do-my-calculus-class",
          icon: faCalculator
        },
        {
          title: "Hire Someone To Take My Statistics Class",
          link: "/hire-someone-to-take-my-statistics-class",
          icon: faChartBar
        },
        {
          title: "Online Class Help by University",
          link: "/online-class-help-services",
          icon: faLaptop
        },
        {
          title: "Someone To Take My Sophia Learning Classes",
          link: "/someone-to-take-my-sophia-learning-classes",
          icon: faGraduationCap
        },
        {
          title: "Physics Class Help Service",
          link: "/physics-class-help-service",
          icon: faAtom
        },
        {
          title: "Take My Online Algebra Class",
          link: "/take-my-online-algebra-class",
          icon: faCalculator
        },
        {
          title: "Take My Online Biology Class",
          link: "/take-my-online-biology-class",
          icon: faAtom
        },
        {
          title: "Take My Online Chemistry Class For Me",
          link: "/take-my-online-chemistry-class-for-me",
          icon: faFlask
        },
        {
          title: "Take My UTA Online Nursing Class",
          link: "/take-my-uta-online-nursing-class",
          icon: faUserMd
        },
        {
          title: "Can Someone Take My WGU Class",
          link: "/take-my-wgu-class",
          icon: faLaptop
        }
      ]
    },
    {
      title: "Assignment Help",
      icon: faBook,
      items: [
        {
          title: "Math Assignment Help Service",
          link: "/math-assignment-help-service",
          icon: faCalculator
        },
        {
          title: "Do My Calculus Homework",
          link: "/do-my-calculus-homework",
          icon: faSquareRootAlt
        },
        {
          title: "Programming Assignment Help Service",
          link: "/programming-assignment-help-service",
          icon: faCode
        },
        {
          title: "Research Paper Writing Service",
          link: "/research-paper-writing-service",
          icon: faSearch
        },
        {
          title: "PowerPoint Presentation Service",
          link: "/powerpoint-presentation-service",
          icon: faDesktop
        },
        {
          title: "Statistics Class Helper",
          link: "/statistics-class-helper",
          icon: faChartBar
        },
        {
          title: "SPSS Assignment Help",
          link: "/spss-assignment-help",
          icon: faChartLine
        },
        {
          title: "Database Management Service",
          link: "/database-management-service",
          icon: faDatabase
        },
        {
          title: "Dissertation Writing Service",
          link: "/dissertation-writing-service",
          icon: faBook
        }
      ]
    },
    {
      title: "Other Services",
      icon: faGraduationCap,
      items: [
        {
          title: "Someone To Take My Online Class",
          link: "/someone-to-take-my-online-class",
          icon: faLaptop
        },
        {
          title: "Urgent Essay Writing Help",
          link: "/urgent-essay-writing-help",
          icon: faClock
        },
        {
          title: "24/7 Homework Help Service",
          link: "/homework-help-service",
          icon: faBookOpen
        },
        {
          title: "Hire Someone To Take My Exam",
          link: "/hire-someone-to-take-my-exam",
          icon: faGraduationCap
        },
        {
          title: "Nursing Essay Writing Service",
          link: "/nursing-essay-writing-service",
          icon: faUserMd
        }
      ]
    }
  ];

  // Flatten service items for backward compatibility
  const serviceItems = serviceCategories.flatMap(category =>
    category.items.map(item => ({
      ...item,
      category: category.title
    }))
  );

  // Check if current path is a service page
  const isServicePage = serviceItems.some(service => service.link === location.pathname) || location.pathname === '/services';

  // Simple path formatter - no special handling needed with BrowserRouter
  const getFormattedPath = (path: string) => {
    return path;
  };

  return (
    <>
      <style>
        {`
          @media (min-width: 992px) {
            #services-dropdown:hover .dropdown-menu,
            .services-dropdown:hover .dropdown-menu {
              display: block;
              margin-top: 0 !important;
              margin-left: 0 !important;
              top: 100% !important;
              left: 0 !important;
              border-top: none;
              transform: none !important;
              position: absolute;
              z-index: 1030;
            }
            .dropdown-menu {
              margin-top: 0;
            }
            .navbar-nav .nav-item.dropdown:hover .dropdown-menu {
              margin-top: 0 !important;
            }
          }
        `}
      </style>
      <Navbar 
        collapseOnSelect 
        expand="lg" 
        variant="light" 
        className={`main-navigation ${scrolled ? 'scrolled' : ''} ${isOpen ? 'menu-open' : ''}`}
        sticky="top"
      >
        <Container>
          <Navbar.Brand>
            <Link
              to="/"
              className="navbar-brand"
              onClick={(e) => handleLinkClick('/', e)}
              aria-label="Homework Guy Logo - Homepage"
            >
              <HomeworkGuyLogo />
            </Link>
          </Navbar.Brand>
          
          <Navbar.Toggle 
            aria-controls="responsive-navbar-nav" 
            className="d-lg-none"
            onClick={toggleMenu}
          >
            <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
          </Navbar.Toggle>
          
          {/* Desktop Navigation */}
          <Navbar.Collapse id="responsive-navbar-nav" className={`d-none d-lg-flex ${isOpen ? 'show' : ''}`}>
            <Nav className="ms-auto">
              <Nav.Item>
                <Link
                  to="/"
                  className={`nav-link ${activeLink === '/' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/', e)}
                >
                  Home
                </Link>
              </Nav.Item>
              
              <NavDropdown
                title="Services"
                id="collapsible-nav-dropdown"
                show={showServicesDropdown}
                onMouseEnter={() => setShowServicesDropdown(true)}
                onMouseLeave={() => {
                  setShowServicesDropdown(false);
                  setHoveredSubmenu(null);
                }}
                onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                className={`services-dropdown ${isServicePage ? 'active' : ''}`}
              >
                <div className="services-nested-menu">
                  {/* Academic Writing Services Submenu */}
                  <div 
                    className="nested-menu-item"
                    onMouseEnter={() => setHoveredSubmenu('academic-writing')}
                    onMouseLeave={() => setHoveredSubmenu(null)}
                  >
                    <div className="submenu-header">
                      <FontAwesomeIcon icon={faPencilAlt} className="submenu-icon" />
                      <span>Academic Writing Services</span>
                      <FontAwesomeIcon icon={faChevronRight} className="chevron-right" />
                    </div>
                    {hoveredSubmenu === 'academic-writing' && (
                      <div 
                        className="submenu-dropdown"
                        onMouseEnter={() => setHoveredSubmenu('academic-writing')}
                        onMouseLeave={() => setHoveredSubmenu(null)}
                      >
                        <Link
                          to={getFormattedPath("/affordable-dissertation-writers")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/affordable-dissertation-writers", e)}
                        >
                          <FontAwesomeIcon icon={faBook} className="item-icon" />
                          Affordable Dissertation Writers
                        </Link>
                        <Link
                          to={getFormattedPath("/dissertation-writing-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/dissertation-writing-service", e)}
                        >
                          <FontAwesomeIcon icon={faBook} className="item-icon" />
                          Dissertation Writing Service
                        </Link>
                        <Link
                          to={getFormattedPath("/last-minute-essay-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/last-minute-essay-help", e)}
                        >
                          <FontAwesomeIcon icon={faClock} className="item-icon" />
                          Last Minute Essay Help
                        </Link>
                        <Link
                          to={getFormattedPath("/pay-someone-to-write-my-essay-cheap")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/pay-someone-to-write-my-essay-cheap", e)}
                        >
                          <FontAwesomeIcon icon={faPencilAlt} className="item-icon" />
                          Pay Someone To Write My Essay Cheap
                        </Link>
                        <Link
                          to={getFormattedPath("/research-paper-writing-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/research-paper-writing-service", e)}
                        >
                          <FontAwesomeIcon icon={faSearch} className="item-icon" />
                          Research Paper Writing Service
                        </Link>
                        <Link
                          to={getFormattedPath("/urgent-annotated-bibliography-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/urgent-annotated-bibliography-service", e)}
                        >
                          <FontAwesomeIcon icon={faBookOpen} className="item-icon" />
                          Urgent Annotated Bibliography Service
                        </Link>
                        <Link
                          to={getFormattedPath("/urgent-essay-writing-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/urgent-essay-writing-help", e)}
                        >
                          <FontAwesomeIcon icon={faClock} className="item-icon" />
                          Urgent Essay Writing Help
                        </Link>
                        <Link
                          to={getFormattedPath("/write-my-case-study")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/write-my-case-study", e)}
                        >
                          <FontAwesomeIcon icon={faFileAlt} className="item-icon" />
                          Write My Case Study
                        </Link>
                        <Link
                          to={getFormattedPath("/nursing-essay-writing-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/nursing-essay-writing-service", e)}
                        >
                          <FontAwesomeIcon icon={faUserMd} className="item-icon" />
                          Nursing Essay Writing Service
                        </Link>
                      </div>
                    )}
                  </div>
                  
                  {/* Coursework Help Submenu */}
                  <div 
                    className="nested-menu-item"
                    onMouseEnter={() => setHoveredSubmenu('coursework')}
                    onMouseLeave={() => setHoveredSubmenu(null)}
                  >
                    <div className="submenu-header">
                      <FontAwesomeIcon icon={faLaptop} className="submenu-icon" />
                      <span>Coursework Help</span>
                      <FontAwesomeIcon icon={faChevronRight} className="chevron-right" />
                    </div>
                    {hoveredSubmenu === 'coursework' && (
                      <div 
                        className="submenu-dropdown"
                        onMouseEnter={() => setHoveredSubmenu('coursework')}
                        onMouseLeave={() => setHoveredSubmenu(null)}
                      >
                        {serviceCategories[0].items.map((service, serviceIndex) => (
                          <Link
                            key={serviceIndex}
                            to={getFormattedPath(service.link)}
                            className="submenu-item"
                            onClick={(e) => handleLinkClick(service.link, e)}
                          >
                            <FontAwesomeIcon icon={service.icon} className="item-icon" />
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Online Exam Help Services Submenu */}
                  <div 
                    className="nested-menu-item"
                    onMouseEnter={() => setHoveredSubmenu('exam-help')}
                    onMouseLeave={() => setHoveredSubmenu(null)}
                  >
                    <div className="submenu-header">
                      <FontAwesomeIcon icon={faVideo} className="submenu-icon" />
                      <span>Online Exam Help Services</span>
                      <FontAwesomeIcon icon={faChevronRight} className="chevron-right" />
                    </div>
                    {hoveredSubmenu === 'exam-help' && (
                      <div 
                        className="submenu-dropdown"
                        onMouseEnter={() => setHoveredSubmenu('exam-help')}
                        onMouseLeave={() => setHoveredSubmenu(null)}
                      >
                        <Link
                          to={getFormattedPath("/take-proctored-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/take-proctored-exam", e)}
                          style={{ fontWeight: 600 }}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} className="item-icon" />
                          Take Proctored Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-honorlock-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-honorlock-exam", e)}
                        >
                          <FontAwesomeIcon icon={faVideo} className="item-icon" />
                          Do My Honorlock Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-statistics-honorlock-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-statistics-honorlock-exam", e)}
                        >
                          <FontAwesomeIcon icon={faChartBar} className="item-icon" />
                          Do My Statistics Honorlock Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-anatomy-honorlock-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-anatomy-honorlock-exam", e)}
                        >
                          <FontAwesomeIcon icon={faUserMd} className="item-icon" />
                          Do My Anatomy Honorlock Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-respondus-lockdown-browser-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-respondus-lockdown-browser-exam", e)}
                        >
                          <FontAwesomeIcon icon={faLaptop} className="item-icon" />
                          Do My Respondus Lockdown Browser Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/take-my-proctorio-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/take-my-proctorio-exam", e)}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} className="item-icon" />
                          Take My Proctorio Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/calculus-honorlock-exam-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/calculus-honorlock-exam-help", e)}
                        >
                          <FontAwesomeIcon icon={faCalculator} className="item-icon" />
                          Calculus Honorlock Exam Help
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-physics-exam-online")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-physics-exam-online", e)}
                        >
                          <FontAwesomeIcon icon={faAtom} className="item-icon" />
                          Do My Physics Exam Online
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-proctored-exam-for-me")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-proctored-exam-for-me", e)}
                        >
                          <FontAwesomeIcon icon={faFileAlt} className="item-icon" />
                          Do My Proctored Exam For Me
                        </Link>
                        <Link
                          to={getFormattedPath("/hire-someone-to-take-my-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/hire-someone-to-take-my-exam", e)}
                        >
                          <FontAwesomeIcon icon={faGraduationCap} className="item-icon" />
                          Hire Someone To Take My Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/hire-someone-to-take-my-history-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/hire-someone-to-take-my-history-exam", e)}
                        >
                          <FontAwesomeIcon icon={faHistory} className="item-icon" />
                          Hire Someone To Take My History Exam
                        </Link>
                      </div>
                    )}
                  </div>
                  
                  {/* Homework/Assignment Help Submenu */}
                  <div 
                    className="nested-menu-item"
                    onMouseEnter={() => setHoveredSubmenu('homework-help')}
                    onMouseLeave={() => setHoveredSubmenu(null)}
                  >
                    <div className="submenu-header">
                      <FontAwesomeIcon icon={faBook} className="submenu-icon" />
                      <span>Homework/Assignment Help</span>
                      <FontAwesomeIcon icon={faChevronRight} className="chevron-right" />
                    </div>
                    {hoveredSubmenu === 'homework-help' && (
                      <div 
                        className="submenu-dropdown"
                        onMouseEnter={() => setHoveredSubmenu('homework-help')}
                        onMouseLeave={() => setHoveredSubmenu(null)}
                      >
                        <Link
                          to={getFormattedPath("/affordable-accounting-assignment-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/affordable-accounting-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faCalculator} className="item-icon" />
                          Affordable Accounting Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/autocad-assignment-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/autocad-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faDesktop} className="item-icon" />
                          AutoCAD Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/best-python-assignment-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/best-python-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faCode} className="item-icon" />
                          Best Python Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/database-assignment-help-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/database-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faDatabase} className="item-icon" />
                          Database Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/hire-someone-to-take-my-history-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/hire-someone-to-take-my-history-exam", e)}
                        >
                          <FontAwesomeIcon icon={faHistory} className="item-icon" />
                          Hire Someone To Take My History Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-calculus-homework")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-calculus-homework", e)}
                        >
                          <FontAwesomeIcon icon={faSquareRootAlt} className="item-icon" />
                          Do My Calculus Homework
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-computer-science-assignment")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/do-my-computer-science-assignment", e)}
                        >
                          <FontAwesomeIcon icon={faDesktop} className="item-icon" />
                          Do My Computer Science Assignment
                        </Link>
                        <Link
                          to={getFormattedPath("/help-with-uta-rn-to-bsn-assignments")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/help-with-uta-rn-to-bsn-assignments", e)}
                        >
                          <FontAwesomeIcon icon={faUserMd} className="item-icon" />
                          Help With UTA RN To BSN Assignments
                        </Link>
                        <Link
                          to={getFormattedPath("/programming-assignment-help-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/programming-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faCode} className="item-icon" />
                          Programming Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/r-studio-assignment-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/r-studio-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="item-icon" />
                          R Studio Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/spss-assignment-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/spss-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="item-icon" />
                          SPSS Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/statistics-assignment-help-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/statistics-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faChartBar} className="item-icon" />
                          Statistics Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/urgent-calculus-assignment-help-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/urgent-calculus-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faClock} className="item-icon" />
                          Urgent Calculus Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/powerpoint-presentation-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/powerpoint-presentation-service", e)}
                        >
                          <FontAwesomeIcon icon={faDesktop} className="item-icon" />
                          PowerPoint Presentation Service
                        </Link>
                        <Link
                          to={getFormattedPath("/physics-homework-help-service")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/physics-homework-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faAtom} className="item-icon" />
                          Physics Homework Help Service
                        </Link>
                      </div>
                    )}
                  </div>
                  
                  {/* Universities Submenu */}
                  <div
                    className="nested-menu-item"
                    onMouseEnter={() => setHoveredSubmenu('universities')}
                    onMouseLeave={() => setHoveredSubmenu(null)}
                  >
                    <div className="submenu-header">
                      <FontAwesomeIcon icon={faUniversity} className="submenu-icon" />
                      <span>Universities</span>
                      <FontAwesomeIcon icon={faChevronRight} className="chevron-right" />
                    </div>
                    {hoveredSubmenu === 'universities' && (
                      <div
                        className="submenu-dropdown"
                        onMouseEnter={() => setHoveredSubmenu('universities')}
                        onMouseLeave={() => setHoveredSubmenu(null)}
                      >
                        <Link
                          to={getFormattedPath("/take-my-capella-university-online-courses")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/take-my-capella-university-online-courses", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="item-icon" />
                          Capella University Online Courses
                        </Link>
                        <Link
                          to={getFormattedPath("/take-my-wgu-courses")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/take-my-wgu-courses", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="item-icon" />
                          WGU OA & PA Assessment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/someone-to-take-my-snhu-online-class")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/someone-to-take-my-snhu-online-class", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="item-icon" />
                          SNHU Online Class Help
                        </Link>
                        <Link
                          to={getFormattedPath("/umpi-online-class-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/umpi-online-class-help", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="item-icon" />
                          UMPI Online Class Help
                        </Link>
                        <Link
                          to={getFormattedPath("/umgc-online-class-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/umgc-online-class-help", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="item-icon" />
                          UMGC Online Class Help
                        </Link>
                        <Link
                          to={getFormattedPath("/asu-online-class-help")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/asu-online-class-help", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="item-icon" />
                          ASU Online Class Help
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Standardized & Professional Exams Submenu */}
                  <div
                    className="nested-menu-item"
                    onMouseEnter={() => setHoveredSubmenu('standardized-exams')}
                    onMouseLeave={() => setHoveredSubmenu(null)}
                  >
                    <div className="submenu-header">
                      <FontAwesomeIcon icon={faGraduationCap} className="submenu-icon" />
                      <span>Standardized & Professional Exams</span>
                      <FontAwesomeIcon icon={faChevronRight} className="chevron-right" />
                    </div>
                    {hoveredSubmenu === 'standardized-exams' && (
                      <div
                        className="submenu-dropdown"
                        onMouseEnter={() => setHoveredSubmenu('standardized-exams')}
                        onMouseLeave={() => setHoveredSubmenu(null)}
                      >
                        <Link
                          to={getFormattedPath("/someone-to-take-my-gmat-exam")}
                          className="submenu-item"
                          onClick={(e) => handleLinkClick("/someone-to-take-my-gmat-exam", e)}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="item-icon" />
                          GMAT Exam Help
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Someone To Take My Online Class Link */}
                  <Link
                    to={getFormattedPath("/someone-to-take-my-online-class")}
                    className="submenu-header standalone-item"
                    onClick={(e) => handleLinkClick("/someone-to-take-my-online-class", e)}
                  >
                    <FontAwesomeIcon icon={faLaptop} className="submenu-icon" />
                    <span>Someone To Take My Online Class</span>
                  </Link>
                  
                  {/* View All Services Link */}
                  <Link
                    to="/services"
                    className="view-all-services-nested"
                    onClick={(e) => handleLinkClick('/services', e)}
                  >
                    View All Services <FontAwesomeIcon icon={faChevronRight} />
                  </Link>
                </div>
              </NavDropdown>
              
              <Nav.Item>
                <Link
                  to="/how-it-works"
                  className={`nav-link ${activeLink === '/how-it-works' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/how-it-works', e)}
                >
                  How It Works
                </Link>
              </Nav.Item>
              
              <NavDropdown
                title="Resources"
                id="resources-dropdown"
                show={showResourcesDropdown}
                onMouseEnter={() => setShowResourcesDropdown(true)}
                onMouseLeave={() => setShowResourcesDropdown(false)}
                onClick={() => setShowResourcesDropdown(!showResourcesDropdown)}
                className={`resources-dropdown ${(activeLink === '/blog' || activeLink === '/summarizer' || activeLink === '/sample-papers') ? 'active' : ''}`}
              >
                <Link
                  to="/blog"
                  className="dropdown-item"
                  onClick={(e) => handleLinkClick('/blog', e)}
                >
                  <FontAwesomeIcon icon={faBookOpen} className="me-2" />
                  Blog
                </Link>
                <Link
                  to="/sample-papers"
                  className="dropdown-item"
                  onClick={(e) => handleLinkClick('/sample-papers', e)}
                >
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                  Sample Papers
                </Link>
                <Link
                  to="/summarizer"
                  className="dropdown-item"
                  onClick={(e) => handleLinkClick('/summarizer', e)}
                >
                  <FontAwesomeIcon icon={faMagic} className="me-2" />
                  Summarizer
                </Link>
              </NavDropdown>
              
              <Nav.Item>
                <Link
                  to="/contact"
                  className={`nav-link ${activeLink === '/contact' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/contact', e)}
                >
                  Contact
                </Link>
              </Nav.Item>

              <Nav.Item>
                <Link
                  to="/affiliate"
                  className={`nav-link earn-cta-link ${activeLink === '/affiliate' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/affiliate', e)}
                >
                  <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                  Earn $1500
                </Link>
              </Nav.Item>
              
              <Nav.Item className="order-nav-item">
                <Link
                  to="/order"
                  className="nav-link order-link"
                  onClick={(e) => handleLinkClick('/order', e)}
                >
                  Order Now
                </Link>
              </Nav.Item>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Mobile Side Shutter */}
      {isMobile && (
        <>
          {/* Overlay */}
          <div 
            className={`mobile-overlay ${isOpen ? 'show' : ''}`}
            onClick={closeMobileMenu}
          />
          
          {/* Side Shutter */}
          <div className={`mobile-side-shutter ${isOpen ? 'open' : ''}`}>
            <div className="mobile-shutter-header">
              <button 
                className="mobile-close-btn"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="mobile-shutter-content">
              <nav className="mobile-nav">
                <Link
                  to="/"
                  className={`mobile-nav-link ${activeLink === '/' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/', e)}
                >
                  Home
                </Link>
                
                <div className="mobile-services-section">
                  <button 
                    className={`mobile-services-toggle ${mobileServicesOpen ? 'open' : ''}`}
                    onClick={toggleMobileServices}
                  >
                    Services
                    <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                  </button>
                  
                  <div className={`mobile-services-list ${mobileServicesOpen ? 'open' : ''}`}>
                    {/* Academic Writing Services Nested Menu */}
                    <div className="mobile-nested-menu-item">
                      <button
                        className={`mobile-nested-menu-toggle ${mobileNestedMenuOpen === 'academic-writing' ? 'open' : ''}`}
                        onClick={() => toggleMobileNestedMenu('academic-writing')}
                      >
                        <FontAwesomeIcon icon={faPencilAlt} className="service-icon" />
                        <span>Academic Writing Services</span>
                        <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                      </button>
                      <div className={`mobile-nested-menu-items ${mobileNestedMenuOpen === 'academic-writing' ? 'open' : ''}`}>
                        <Link
                          to={getFormattedPath("/affordable-dissertation-writers")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/affordable-dissertation-writers", e)}
                        >
                          <FontAwesomeIcon icon={faBook} className="service-icon" />
                          Affordable Dissertation Writers
                        </Link>
                        <Link
                          to={getFormattedPath("/dissertation-writing-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/dissertation-writing-service", e)}
                        >
                          <FontAwesomeIcon icon={faBook} className="service-icon" />
                          Dissertation Writing Service
                        </Link>
                        <Link
                          to={getFormattedPath("/last-minute-essay-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/last-minute-essay-help", e)}
                        >
                          <FontAwesomeIcon icon={faClock} className="service-icon" />
                          Last Minute Essay Help
                        </Link>
                        <Link
                          to={getFormattedPath("/pay-someone-to-write-my-essay-cheap")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/pay-someone-to-write-my-essay-cheap", e)}
                        >
                          <FontAwesomeIcon icon={faPencilAlt} className="service-icon" />
                          Pay Someone To Write My Essay Cheap
                        </Link>
                        <Link
                          to={getFormattedPath("/research-paper-writing-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/research-paper-writing-service", e)}
                        >
                          <FontAwesomeIcon icon={faSearch} className="service-icon" />
                          Research Paper Writing Service
                        </Link>
                        <Link
                          to={getFormattedPath("/urgent-annotated-bibliography-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/urgent-annotated-bibliography-service", e)}
                        >
                          <FontAwesomeIcon icon={faBookOpen} className="service-icon" />
                          Urgent Annotated Bibliography Service
                        </Link>
                        <Link
                          to={getFormattedPath("/urgent-essay-writing-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/urgent-essay-writing-help", e)}
                        >
                          <FontAwesomeIcon icon={faClock} className="service-icon" />
                          Urgent Essay Writing Help
                        </Link>
                        <Link
                          to={getFormattedPath("/write-my-case-study")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/write-my-case-study", e)}
                        >
                          <FontAwesomeIcon icon={faFileAlt} className="service-icon" />
                          Write My Case Study
                        </Link>
                        <Link
                          to={getFormattedPath("/nursing-essay-writing-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/nursing-essay-writing-service", e)}
                        >
                          <FontAwesomeIcon icon={faUserMd} className="service-icon" />
                          Nursing Essay Writing Service
                        </Link>
                      </div>
                    </div>

                    {/* Coursework Help Nested Menu */}
                    <div className="mobile-nested-menu-item">
                      <button
                        className={`mobile-nested-menu-toggle ${mobileNestedMenuOpen === 'coursework' ? 'open' : ''}`}
                        onClick={() => toggleMobileNestedMenu('coursework')}
                      >
                        <FontAwesomeIcon icon={faLaptop} className="service-icon" />
                        <span>Coursework Help</span>
                        <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                      </button>
                      <div className={`mobile-nested-menu-items ${mobileNestedMenuOpen === 'coursework' ? 'open' : ''}`}>
                        {serviceCategories[0].items.map((service, serviceIndex) => (
                          <Link
                            key={serviceIndex}
                            to={getFormattedPath(service.link)}
                            className="mobile-service-item"
                            onClick={(e) => handleLinkClick(service.link, e)}
                          >
                            <FontAwesomeIcon icon={service.icon} className="service-icon" />
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Online Exam Help Services Nested Menu */}
                    <div className="mobile-nested-menu-item">
                      <button
                        className={`mobile-nested-menu-toggle ${mobileNestedMenuOpen === 'exam-help' ? 'open' : ''}`}
                        onClick={() => toggleMobileNestedMenu('exam-help')}
                      >
                        <FontAwesomeIcon icon={faVideo} className="service-icon" />
                        <span>Online Exam Help Services</span>
                        <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                      </button>
                      <div className={`mobile-nested-menu-items ${mobileNestedMenuOpen === 'exam-help' ? 'open' : ''}`}>
                        <Link
                          to={getFormattedPath("/take-proctored-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/take-proctored-exam", e)}
                          style={{ fontWeight: 600 }}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} className="service-icon" />
                          Take Proctored Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-honorlock-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-honorlock-exam", e)}
                        >
                          <FontAwesomeIcon icon={faVideo} className="service-icon" />
                          Do My Honorlock Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-statistics-honorlock-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-statistics-honorlock-exam", e)}
                        >
                          <FontAwesomeIcon icon={faChartBar} className="service-icon" />
                          Do My Statistics Honorlock Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-anatomy-honorlock-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-anatomy-honorlock-exam", e)}
                        >
                          <FontAwesomeIcon icon={faUserMd} className="service-icon" />
                          Do My Anatomy Honorlock Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-respondus-lockdown-browser-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-respondus-lockdown-browser-exam", e)}
                        >
                          <FontAwesomeIcon icon={faLaptop} className="service-icon" />
                          Do My Respondus Lockdown Browser Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/take-my-proctorio-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/take-my-proctorio-exam", e)}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} className="service-icon" />
                          Take My Proctorio Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/calculus-honorlock-exam-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/calculus-honorlock-exam-help", e)}
                        >
                          <FontAwesomeIcon icon={faCalculator} className="service-icon" />
                          Calculus Honorlock Exam Help
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-physics-exam-online")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-physics-exam-online", e)}
                        >
                          <FontAwesomeIcon icon={faAtom} className="service-icon" />
                          Do My Physics Exam Online
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-proctored-exam-for-me")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-proctored-exam-for-me", e)}
                        >
                          <FontAwesomeIcon icon={faFileAlt} className="service-icon" />
                          Do My Proctored Exam For Me
                        </Link>
                        <Link
                          to={getFormattedPath("/hire-someone-to-take-my-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/hire-someone-to-take-my-exam", e)}
                        >
                          <FontAwesomeIcon icon={faGraduationCap} className="service-icon" />
                          Hire Someone To Take My Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/hire-someone-to-take-my-history-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/hire-someone-to-take-my-history-exam", e)}
                        >
                          <FontAwesomeIcon icon={faHistory} className="service-icon" />
                          Hire Someone To Take My History Exam
                        </Link>
                      </div>
                    </div>

                    {/* Homework/Assignment Help Nested Menu */}
                    <div className="mobile-nested-menu-item">
                      <button
                        className={`mobile-nested-menu-toggle ${mobileNestedMenuOpen === 'homework-help' ? 'open' : ''}`}
                        onClick={() => toggleMobileNestedMenu('homework-help')}
                      >
                        <FontAwesomeIcon icon={faBook} className="service-icon" />
                        <span>Homework/Assignment Help</span>
                        <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                      </button>
                      <div className={`mobile-nested-menu-items ${mobileNestedMenuOpen === 'homework-help' ? 'open' : ''}`}>
                        <Link
                          to={getFormattedPath("/affordable-accounting-assignment-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/affordable-accounting-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faCalculator} className="service-icon" />
                          Affordable Accounting Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/autocad-assignment-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/autocad-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faDesktop} className="service-icon" />
                          AutoCAD Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/best-python-assignment-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/best-python-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faCode} className="service-icon" />
                          Best Python Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/database-assignment-help-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/database-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faDatabase} className="service-icon" />
                          Database Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/hire-someone-to-take-my-history-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/hire-someone-to-take-my-history-exam", e)}
                        >
                          <FontAwesomeIcon icon={faHistory} className="service-icon" />
                          Hire Someone To Take My History Exam
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-calculus-homework")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-calculus-homework", e)}
                        >
                          <FontAwesomeIcon icon={faSquareRootAlt} className="service-icon" />
                          Do My Calculus Homework
                        </Link>
                        <Link
                          to={getFormattedPath("/do-my-computer-science-assignment")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/do-my-computer-science-assignment", e)}
                        >
                          <FontAwesomeIcon icon={faDesktop} className="service-icon" />
                          Do My Computer Science Assignment
                        </Link>
                        <Link
                          to={getFormattedPath("/help-with-uta-rn-to-bsn-assignments")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/help-with-uta-rn-to-bsn-assignments", e)}
                        >
                          <FontAwesomeIcon icon={faUserMd} className="service-icon" />
                          Help With UTA RN To BSN Assignments
                        </Link>
                        <Link
                          to={getFormattedPath("/programming-assignment-help-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/programming-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faCode} className="service-icon" />
                          Programming Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/r-studio-assignment-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/r-studio-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="service-icon" />
                          R Studio Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/spss-assignment-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/spss-assignment-help", e)}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="service-icon" />
                          SPSS Assignment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/statistics-assignment-help-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/statistics-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faChartBar} className="service-icon" />
                          Statistics Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/urgent-calculus-assignment-help-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/urgent-calculus-assignment-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faClock} className="service-icon" />
                          Urgent Calculus Assignment Help Service
                        </Link>
                        <Link
                          to={getFormattedPath("/powerpoint-presentation-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/powerpoint-presentation-service", e)}
                        >
                          <FontAwesomeIcon icon={faDesktop} className="service-icon" />
                          PowerPoint Presentation Service
                        </Link>
                        <Link
                          to={getFormattedPath("/physics-homework-help-service")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/physics-homework-help-service", e)}
                        >
                          <FontAwesomeIcon icon={faAtom} className="service-icon" />
                          Physics Homework Help Service
                        </Link>
                      </div>
                    </div>

                    {/* Universities Nested Menu */}
                    <div className="mobile-nested-menu-item">
                      <button
                        className={`mobile-nested-menu-toggle ${mobileNestedMenuOpen === 'universities' ? 'open' : ''}`}
                        onClick={() => toggleMobileNestedMenu('universities')}
                      >
                        <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                        <span>Universities</span>
                        <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                      </button>
                      <div className={`mobile-nested-menu-items ${mobileNestedMenuOpen === 'universities' ? 'open' : ''}`}>
                        <Link
                          to={getFormattedPath("/take-my-capella-university-online-courses")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/take-my-capella-university-online-courses", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                          Capella University Online Courses
                        </Link>
                        <Link
                          to={getFormattedPath("/take-my-wgu-courses")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/take-my-wgu-courses", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                          WGU OA & PA Assessment Help
                        </Link>
                        <Link
                          to={getFormattedPath("/someone-to-take-my-snhu-online-class")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/someone-to-take-my-snhu-online-class", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                          SNHU Online Class Help
                        </Link>
                        <Link
                          to={getFormattedPath("/umpi-online-class-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/umpi-online-class-help", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                          UMPI Online Class Help
                        </Link>
                        <Link
                          to={getFormattedPath("/umgc-online-class-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/umgc-online-class-help", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                          UMGC Online Class Help
                        </Link>
                        <Link
                          to={getFormattedPath("/asu-online-class-help")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/asu-online-class-help", e)}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="service-icon" />
                          ASU Online Class Help
                        </Link>
                      </div>
                    </div>

                    {/* Standardized & Professional Exams Nested Menu */}
                    <div className="mobile-nested-menu-item">
                      <button
                        className={`mobile-nested-menu-toggle ${mobileNestedMenuOpen === 'standardized-exams' ? 'open' : ''}`}
                        onClick={() => toggleMobileNestedMenu('standardized-exams')}
                      >
                        <FontAwesomeIcon icon={faGraduationCap} className="service-icon" />
                        <span>Standardized & Professional Exams</span>
                        <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                      </button>
                      <div className={`mobile-nested-menu-items ${mobileNestedMenuOpen === 'standardized-exams' ? 'open' : ''}`}>
                        <Link
                          to={getFormattedPath("/someone-to-take-my-gmat-exam")}
                          className="mobile-service-item"
                          onClick={(e) => handleLinkClick("/someone-to-take-my-gmat-exam", e)}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="service-icon" />
                          GMAT Exam Help
                        </Link>
                      </div>
                    </div>

                    {/* Someone To Take My Online Class Link */}
                    <Link
                      to={getFormattedPath("/someone-to-take-my-online-class")}
                      className="mobile-nested-menu-toggle standalone-item"
                      onClick={(e) => handleLinkClick("/someone-to-take-my-online-class", e)}
                    >
                      <FontAwesomeIcon icon={faLaptop} className="service-icon" />
                      <span>Someone To Take My Online Class</span>
                    </Link>

                    <Link
                      to="/services"
                      className="mobile-view-all-services"
                      onClick={(e) => handleLinkClick('/services', e)}
                    >
                      <FontAwesomeIcon icon={faChevronRight} className="service-icon" />
                      View All Services
                    </Link>
                  </div>
                </div>
                
                <Link
                  to="/how-it-works"
                  className={`mobile-nav-link ${activeLink === '/how-it-works' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/how-it-works', e)}
                >
                  How It Works
                </Link>
                
                <div className="mobile-resources-section">
                  <button 
                    className={`mobile-resources-toggle ${mobileResourcesOpen ? 'open' : ''}`}
                    onClick={toggleMobileResources}
                  >
                    Resources
                    <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                  </button>
                  
                  <div className={`mobile-resources-list ${mobileResourcesOpen ? 'open' : ''}`}>
                <Link
                  to="/blog"
                      className="mobile-resource-item"
                  onClick={(e) => handleLinkClick('/blog', e)}
                >
                      <FontAwesomeIcon icon={faBookOpen} className="resource-icon" />
                  Blog
                </Link>
                    <Link
                      to="/sample-papers"
                      className="mobile-resource-item"
                      onClick={(e) => handleLinkClick('/sample-papers', e)}
                    >
                      <FontAwesomeIcon icon={faFilePdf} className="resource-icon" />
                      Sample Papers
                    </Link>
                    <Link
                      to="/summarizer"
                      className="mobile-resource-item"
                      onClick={(e) => handleLinkClick('/summarizer', e)}
                    >
                      <FontAwesomeIcon icon={faMagic} className="resource-icon" />
                      Summarizer
                    </Link>
                  </div>
                </div>
                
                <Link
                  to="/contact"
                  className={`mobile-nav-link ${activeLink === '/contact' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/contact', e)}
                >
                  Contact
                </Link>

                <Link
                  to="/affiliate"
                  className={`mobile-nav-link mobile-earn-cta-link ${activeLink === '/affiliate' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/affiliate', e)}
                >
                  <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                  Earn $1500
                </Link>
                
                <Link
                  to="/order"
                  className="mobile-order-btn"
                  onClick={(e) => handleLinkClick('/order', e)}
                >
                  Order Now
                </Link>
              </nav>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navigation; 